import { Trans, useLingui } from '@lingui/react/macro';
import { createColumnHelper } from '@tanstack/react-table';
import { Badge, Button } from '@utima/ui';
import { Eye, EyeOff, GitMerge, Pencil } from 'lucide-react';
import { useMemo } from 'react';

import { ActionsCellWrapper } from '@/components/dataTable/cells/ActionsCellWrapper';
import { SimpleTooltip } from '@/components/tooltips/simple-tooltip';
import { useEnumTranslations } from '@/hooks';
import type { RouterOutputs } from '@/services/trpc';

import { useFeedAttributeMutation } from './useFeedAttributeMutation';

export type FeedAttributeRow =
  RouterOutputs['shopProductAttributes']['listByFeed'][number];

const columnHelper = createColumnHelper<FeedAttributeRow>();

export type UseFeedAttributesColsOptions = {
  onRename: (attribute: FeedAttributeRow) => void;
  onMerge: (attribute: FeedAttributeRow) => void;
};

export function useFeedAttributesCols({
  onRename,
  onMerge,
}: UseFeedAttributesColsOptions) {
  const { t } = useLingui();
  const enumTranslations = useEnumTranslations();
  const { setActive, update } = useFeedAttributeMutation();

  return useMemo(
    () => [
      columnHelper.accessor(row => row.displayName ?? row.name, {
        id: 'name',
        header: t`Attribute`,
        cell: ({ row }) => {
          const data = row.original;

          return (
            <div className='flex min-w-56 flex-col gap-1'>
              <div className='flex flex-wrap items-center gap-1.5'>
                <span className='font-medium leading-tight'>
                  {data.displayName ?? data.name}
                </span>
                {!data.active && (
                  <Badge size='xs' variant='warning'>
                    <Trans>Switched off for this feed</Trans>
                  </Badge>
                )}
                {data.active && data.activeValuesCount === 0 && (
                  <Badge size='xs' outline variant='danger'>
                    <Trans>No active values</Trans>
                  </Badge>
                )}
                {!data.filterable && (
                  <Badge size='xs' outline variant='muted'>
                    <Trans>Not filterable</Trans>
                  </Badge>
                )}
              </div>
              <span className='text-xs text-gray-600'>{data.name}</span>
              {!!data.aliases?.length && (
                <span className='text-xs text-gray-500'>
                  {t`Also known as: ${data.aliases.join(', ')}`}
                </span>
              )}
            </div>
          );
        },
        meta: {
          sortable: 'name',
        },
      }),
      columnHelper.display({
        id: 'dataType',
        header: t`Type`,
        cell: ({ row }) => {
          const data = row.original;

          return (
            <div className='flex flex-col gap-1'>
              <Badge size='sm' outline variant='primary'>
                {enumTranslations.shopAttributeDataType[data.dataType]}
              </Badge>
              {data.unit && (
                <span className='text-xs text-gray-600'>{data.unit}</span>
              )}
              {data.baseUnit && data.baseUnit !== data.unit && (
                <span className='text-xs text-gray-500'>
                  {t`Compared in ${data.baseUnit}`}
                </span>
              )}
            </div>
          );
        },
      }),
      columnHelper.display({
        id: 'usage',
        header: t`Usage`,
        cell: ({ row }) => {
          const data = row.original;

          return (
            <div className='flex flex-col gap-0.5 text-xs'>
              <span className='font-medium'>{t`${data.productsCount} products`}</span>
              <span className='text-gray-600'>{t`${data.activeValuesCount} active values`}</span>
              {data.inactiveValuesCount > 0 && (
                <span className='text-gray-500'>
                  {t`${data.inactiveValuesCount} removed on single products`}
                </span>
              )}
            </div>
          );
        },
        meta: {
          sortable: 'productsCount',
        },
      }),
      columnHelper.display({
        id: 'categories',
        header: t`Categories`,
        cell: ({ row }) => {
          const { categories } = row.original;

          if (categories.length === 0) {
            return (
              <span className='text-xs text-gray-500'>
                <Trans>Not used on any category yet</Trans>
              </span>
            );
          }

          return (
            <div className='flex max-w-72 flex-wrap gap-1'>
              {categories.map(category => (
                <Badge key={category.id} size='xs' outline variant='ghost'>
                  {category.label}
                </Badge>
              ))}
            </div>
          );
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: t`Actions`,
        cell: ({ row }) => {
          const data = row.original;

          return (
            <ActionsCellWrapper>
              <SimpleTooltip title={t`Rename`}>
                <Button
                  data-cta='admin-feed-attribute-rename-button'
                  variant='ghost'
                  outline
                  size='icon-xs'
                  onClick={() => onRename(data)}
                  aria-label={t`Rename`}
                >
                  <Pencil className='size-4' />
                </Button>
              </SimpleTooltip>
              <SimpleTooltip title={t`Merge into another attribute`}>
                <Button
                  data-cta='admin-feed-attribute-merge-button'
                  variant='ghost'
                  outline
                  size='icon-xs'
                  onClick={() => onMerge(data)}
                  aria-label={t`Merge into another attribute`}
                >
                  <GitMerge className='size-4' />
                </Button>
              </SimpleTooltip>
              {data.active ? (
                <SimpleTooltip title={t`Switch off for the whole feed`}>
                  <Button
                    data-cta='admin-feed-attribute-deactivate-button'
                    variant='ghost'
                    outline
                    size='icon-xs'
                    className='text-danger'
                    loading={
                      update.isPending && update.variables?.id === data.id
                    }
                    onClick={() => setActive({ id: data.id, active: false })}
                    aria-label={t`Switch off for the whole feed`}
                  >
                    <EyeOff className='size-4' />
                  </Button>
                </SimpleTooltip>
              ) : (
                <SimpleTooltip title={t`Switch back on for the whole feed`}>
                  <Button
                    data-cta='admin-feed-attribute-activate-button'
                    variant='ghost'
                    outline
                    size='icon-xs'
                    className='text-success'
                    loading={
                      update.isPending && update.variables?.id === data.id
                    }
                    onClick={() => setActive({ id: data.id, active: true })}
                    aria-label={t`Switch back on for the whole feed`}
                  >
                    <Eye className='size-4' />
                  </Button>
                </SimpleTooltip>
              )}
            </ActionsCellWrapper>
          );
        },
        meta: {
          align: 'center',
        },
      }),
    ],
    [
      t,
      enumTranslations.shopAttributeDataType,
      onRename,
      onMerge,
      setActive,
      update.isPending,
      update.variables?.id,
    ],
  );
}
